/**
 * The TAPES sub-directory, in `localStorage`.
 *
 * The original wrote saved missions to a folder — "Naming the file and pressing
 * Save will automatically save the mission in a sub-directory called TAPES" —
 * and its Tapes panel offered `LOAD NEW TAPE`, `SAVE CURRENT TAPE` and
 * `DELETE TAPE(S)`. This is the same shelf, in the only durable place a browser
 * offers without asking permission first.
 *
 * Storage-agnostic on purpose: `TapeStore` is `getItem`/`setItem`, so the whole
 * round trip is unit-testable against a plain in-memory fake, exactly as
 * `career/career-store.ts` and `audio/settings.ts` do it.
 *
 * ## Why the shelf is capped and the export is not
 *
 * `localStorage` is a few megabytes for the whole origin, shared with the career
 * roster and every settings blob. A tape is small — a quarter-hour keyboard
 * flight is single-digit kilobytes — but a tape cut from a violent
 * stick-waggling sortie is not, and a shelf that silently fills up until
 * *career saves* start failing would be a poor trade for a keepsake. So the
 * shelf holds `MAX_TAPES` and refuses politely, and anything precious goes to a
 * file via `tapeFileName`/`parseTape`, which has no ceiling at all. That is also
 * how the original expected tapes to travel: "you can copy the recorded missions
 * to floppy disk or transfer them by modem to your friends".
 */

import { checkGuard, parseTape, type ReplayTape } from "@red-opus/engine";

export const TAPE_STORAGE_KEY = "red-opus/tapes";

/**
 * How many tapes the shelf holds. A port choice — the original was bounded by
 * the floppy, and warned at "less than 30K of disk space remaining" rather than
 * by a count.
 */
export const MAX_TAPES = 24;

/** Verbatim in spirit with the original's roster refusal, for the screen that has to say no. */
export const SHELF_FULL_MESSAGE = "THE TAPE SHELF IS FULL — DELETE A TAPE FIRST.";

export interface TapeStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * What the tape list can show without loading a whole tape: everything but the
 * trace. The list is the original's `SELECT TAPE TO VIEW:` panel, and it must
 * stay cheap when the shelf is full.
 */
export interface TapeSummary {
  readonly name: string;
  readonly title: string;
  readonly recordedAt: string;
  readonly ticks: number;
  /** True when this build can still replay it — `checkGuard` on the stored guard. */
  readonly playable: boolean;
  readonly reasons: readonly string[];
}

/**
 * Every readable tape. An unreadable one is dropped rather than taking the
 * shelf down with it: a half-written entry from a storage quota failure must
 * not cost a pilot the other twenty-three tapes.
 */
export function loadTapes(store: TapeStore): readonly ReplayTape[] {
  const raw = store.getItem(TAPE_STORAGE_KEY);
  if (raw === null) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.map(readOrDrop).filter((tape): tape is ReplayTape => tape !== undefined);
}

function readOrDrop(value: unknown): ReplayTape | undefined {
  try {
    return parseTape(JSON.stringify(value));
  } catch {
    // The tape is malformed — a truncated write, or a file from a build whose
    // envelope this one cannot read. Nothing can be done with it and nothing is
    // lost by skipping it, because `readTape` has already refused to guess.
    return undefined;
  }
}

function writeAll(store: TapeStore, tapes: readonly ReplayTape[]): void {
  store.setItem(TAPE_STORAGE_KEY, JSON.stringify(tapes));
}

/**
 * The shelf as the list draws it, newest first — the original's tape picker
 * showed the folder, and a pilot looking for the sortie they just flew should
 * find it at the top. The guard is checked here rather than at load, so a stale
 * tape is still listed, still named, and still exportable; it simply cannot be
 * played by this build, and says so.
 */
export function tapeSummaries(store: TapeStore): readonly TapeSummary[] {
  return [...loadTapes(store)]
    .sort((a, b) => b.recordedAt.localeCompare(a.recordedAt))
    .map((tape) => {
      const verdict = checkGuard(tape);
      return {
        name: tape.name,
        title: tape.title,
        recordedAt: tape.recordedAt,
        ticks: tape.ticks,
        playable: verdict.ok,
        reasons: verdict.reasons,
      };
    });
}

export function findTape(store: TapeStore, name: string): ReplayTape | undefined {
  return loadTapes(store).find((tape) => tape.name === name);
}

/**
 * Shelves a tape, replacing any earlier tape of the same name — the original
 * overwrote by filename too, and a pilot who saves `SPLIT_S` twice means the
 * second one. Refuses a *new* name once the shelf is full.
 */
export function saveTape(store: TapeStore, tape: ReplayTape): void {
  const existing = loadTapes(store);
  const known = existing.some((entry) => entry.name === tape.name);
  if (!known && existing.length >= MAX_TAPES) throw new Error(SHELF_FULL_MESSAGE);
  writeAll(store, known ? existing.map((entry) => (entry.name === tape.name ? tape : entry)) : [...existing, tape]);
}

/** `DELETE TAPE(S)` — the Tapes panel's own button. */
export function deleteTape(store: TapeStore, name: string): void {
  writeAll(store, loadTapes(store).filter((tape) => tape.name !== name));
}

/**
 * A name that will not collide with what is already shelved. The original
 * truncated silently and overwrote; this suggests `SPLIT_S2` instead, because
 * losing a flight to a name clash is a worse surprise than an odd digit.
 */
export function freeTapeName(store: TapeStore, proposed: string, maxLength: number): string {
  const taken = new Set(loadTapes(store).map((tape) => tape.name));
  if (!taken.has(proposed)) return proposed;
  for (let suffix = 2; suffix < 100; suffix++) {
    const candidate = proposed.slice(0, maxLength - String(suffix).length) + String(suffix);
    if (!taken.has(candidate)) return candidate;
  }
  return proposed;
}

/** The file name a tape downloads as. `.tape.json` rather than `.VCR`: it is not one, and must never look like one. */
export function tapeFileName(tape: ReplayTape): string {
  return `${tape.name}.tape.json`;
}

/** The browser's `localStorage`, or nothing when there isn't one (SSR, `bun test`). */
export function browserTapeStore(): TapeStore | undefined {
  try {
    return typeof localStorage === "undefined" ? undefined : localStorage;
  } catch {
    return undefined;
  }
}
